<template>
    <div>
        <Popup :showHeader="false" :showPopup="visible" @hidePopup="close">
            <div class="balance-popup">
                <CloseIcon v-active-touch class="close-icon interactive-icon" @click.native="close" />
                <span class="balance-popup-title">{{
                    $t("m.balancePopupTitle")
                }}</span>
                <div class="balance-popup-subtitle">
                    <span>{{
                        $t("m.balancePopupCurrentBalance", {
                            amount: formattedAccount(balance),
                        })
                    }}</span>
                    <span v-if="balanceThreshold > 0">
                        {{
                            $t("m.balancePopupMinRentBalance", {
                                amount: formattedAccountBillingRule(balanceThreshold, billingRule),
                            })
                        }}
                    </span>
                    <span v-if="billingRuleTaxText" class="tax-hint">{{ billingRuleTaxText }}</span>
                </div>
                <div class="recharge-amount-container">
                    <div
                        v-active-touch
                        class="recharge-amount-item interactive-surface"
                        :class="[selectedAmount === item ? 'selected' : '']"
                        v-for="(item, index) in rechargeAmounts"
                        :key="index"
                        @click="selectAmount(item)"
                    >
                        <span class="amount">{{ formattedAccount(item) }}</span>
                        <span class="bonus" v-if="item && item.bonus"
                            >+${{ item.bonus }}</span
                        >
                    </div>
                </div>
                <PaymentMethods
                    v-bind="paymentMethodsProps"
                    :specialAddLabelKey="'m.topUpSaveCard'"
                    @update:selectedMethod="selectedMethod = $event"
                    @update:selectedCardId="selectedCardId = $event"
                    @refresh-wallet="$emit('refresh-wallet')"
                    @show-loading="$emit('show-loading')"
                    @hide-loading="$emit('hide-loading')"
                    @toast="$emit('toast', $event)"
                    @oneclick-add="handleSpecialTopUp"
                    @thawani-pay="handleSpecialTopUp"
                    @bind-card="handleBindCard"
                    @prefetch-bind-card="handlePrefetchBindCard"
                />
                <span
                    v-active-touch
                    class="primary-btn payment-btn"
                    :class="{ invalid: !isTopUpMethodSelected }"
                    @click="submitBalanceRecharge"
                >
                    <span>{{ $t("m.topUpRent") }}</span>
                </span>
            </div>
        </Popup>
        <BindCardPopup
            ref="bindCardPopup"
            :showPopup="isShowBindPopup"
            :cardGatewayId="bindCardGatewayId"
            :stripeGatewayId="stripeGatewayId"
            :fortisGatewayId="fortisGatewayId"
            :stripe="stripe"
            :deviceCode="deviceCode"
            :outletId="outletId"
            :customSubmit="true"
            :fortisClientSecret="bindCardFortisClientSecret"
            @hidePopup="closeBindCardPopup"
            @submit-payment="handleNewCardSubmit"
            @show-loading="$emit('show-loading', bindCardLoadingMeta)"
            @hide-loading="$emit('hide-loading', bindCardLoadingMeta)"
            @toast="$emit('toast', $event)"
        />
    </div>
</template>

<script>
import Popup from "@/components/popups/Popup.vue";
import CloseIcon from "@/components/icons/CloseIcon.vue";
import PaymentMethods from "../components/PaymentMethods.vue";
import BindCardPopup from "./BindCardPopup.vue";
import { doBalancePayment } from "@/api/logined";
import {
    getClientNextActionField,
    handleClientNextAction,
    syncClientNextAction,
} from "@/services/clientNextAction";

export default {
    name: "RechargePopup",
    components: {
        Popup,
        CloseIcon,
        PaymentMethods,
        BindCardPopup,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        balance: {
            type: [Number, String],
            default: 0,
        },
        balanceThreshold: {
            type: [Number, String],
            default: 0,
        },
        rechargeAmounts: {
            type: Array,
            default: () => [],
        },
        paymentMethods: {
            type: Array,
            default: () => [],
        },
        cardList: {
            type: Array,
            default: () => [],
        },
        cardGatewayId: {
            type: [Number, String],
            default: 0,
        },
        stripeGatewayId: {
            type: [Number, String],
            default: 0,
        },
        fortisGatewayId: {
            type: [Number, String],
            default: 0,
        },
        oneclickGatewayId: {
            type: [Number, String],
            default: 0,
        },
        thawaniGatewayId: {
            type: [Number, String],
            default: 0,
        },

        stripe: {
            type: Object,
            default: null,
        },
        selectedSubscription: {
            type: Object,
            default: () => ({}),
        },
        deviceCode: {
            type: String,
            default: "",
        },
        outletId: {
            type: [Number, String],
            default: "",
        },
    },
    data() {
        return {
            selectedAmount: undefined,
            selectedMethod: undefined,
            selectedCardId: undefined,
            isShowBindPopup: false,
            bindCardGatewayId: 0,
            bindCardInternalId: undefined,
            bindCardClientSecret: undefined,
        };
    },
    computed: {
        billingRule() {
            return this.$store.getters.billing_rule || {};
        },
        billingRuleTaxText() {
            return this.getBillingRuleTaxText(this.billingRule);
        },
        paymentMethodsProps() {
            return {
                showTitle: false,
                variant: "divider",
                showBalance: false,
                enableBindCardPopup: false,
                enableSpecialAddAction: true,
                paymentMethods: this.paymentMethods,
                cardList: this.cardList,
                selectedMethod: this.selectedMethod,
                selectedCardId: this.selectedCardId,
                cardGatewayId: this.cardGatewayId,
                stripeGatewayId: this.stripeGatewayId,
                fortisGatewayId: this.fortisGatewayId,
                stripe: this.stripe,
                selectedSubscription: this.selectedSubscription,
                deviceCode: this.deviceCode,
                outletId: this.outletId,
            };
        },
        bindCardFortisClientSecret() {
            if (!this.isShowBindPopup) return undefined;
            if (Number(this.bindCardGatewayId) !== Number(this.fortisGatewayId)) {
                return undefined;
            }
            return this.bindCardClientSecret;
        },
        bindCardLoadingMeta() {
            return {
                source: "recharge",
                loadingScope: "global",
                pay_method_type: "web_card",
                payment_gateway: this.bindCardGatewayId,
                stage: "bind_card",
            };
        },
        isTopUpMethodSelected() {
            if (!this.selectedMethod) return false;
            if (
                this.selectedMethod === "card" ||
                this.selectedMethod === "web_card"
            ) {
                return !!this.selectedCardId;
            }
            return true;
        },
    },
    watch: {
        visible(val) {
            if (val) {
                // Initialize selectedAmount if needed
                if (!this.selectedAmount && this.rechargeAmounts.length > 0) {
                    this.selectedAmount = this.rechargeAmounts[0];
                }
            } else {
                // Reset on close
                this.selectedMethod = undefined;
                this.selectedCardId = undefined;
                this.closeBindCardPopup();
            }
        },
        rechargeAmounts: {
            handler(val) {
                if (val && val.length > 0 && !this.selectedAmount) {
                    this.selectedAmount = val[0];
                }
            },
            immediate: true,
        },
    },
    methods: {
        close() {
            this.$emit("update:visible", false);
        },
        closeBindCardPopup() {
            this.isShowBindPopup = false;
            this.bindCardGatewayId = 0;
            this.bindCardInternalId = undefined;
            this.bindCardClientSecret = undefined;
        },
        selectAmount(amount) {
            this.selectedAmount = amount;
        },
        async doBalancePayment(payload) {
            const normalizeAmount = (value) => {
                if (value && typeof value === "object" && "amount" in value) {
                    return Number(value.amount) || 0;
                }
                return Number(value) || 0;
            };
            const res = await doBalancePayment({
                ...payload,
                amount: normalizeAmount(payload.amount),
            });
            return res;
        },
        getGatewayIdByPayMethodType(payMethodType) {
            if (!payMethodType) return 0;
            if (payMethodType === "oneclick") return Number(this.oneclickGatewayId) || 0;
            if (payMethodType === "thawani") return Number(this.thawaniGatewayId) || 0;
            if (
                payMethodType === "card" ||
                payMethodType === "apple_pay" ||
                payMethodType === "google_pay"
            ) {
                return Number(this.stripeGatewayId) || 0;
            }
            if (
                payMethodType === "web_card" ||
                payMethodType === "web_apple_pay" ||
                payMethodType === "web_google_pay"
            ) {
                return Number(this.fortisGatewayId) || 0;
            }
            return 0;
        },
        async handleSpecialTopUp(method) {
            const payMethodType = method && method.method;
            if (!payMethodType) return;
            this.selectedMethod = payMethodType;
            await this.submitBalanceRecharge();
        },
        async handleBindCard(method) {
            const payMethodType = method && method.method;
            if (!payMethodType) return;
            const gatewayId = this.getGatewayIdByPayMethodType(payMethodType);
            if (!gatewayId) return;
            if (!this.selectedAmount && this.rechargeAmounts.length > 0) {
                this.selectedAmount = this.rechargeAmounts[0];
            }

            const loadingMeta = this.bindCardLoadingMeta;
            let willRedirect = false;
            try {
                this.bindCardGatewayId = gatewayId;
                this.$emit("show-loading", loadingMeta);

                const completeOrderRes = await this.doBalancePayment({
                    pay_method_type: payMethodType,
                    amount: this.selectedAmount,
                    code: this.deviceCode,
                    gateway_id: gatewayId,
                    card_id: 0,
                });

                syncClientNextAction(this, completeOrderRes, {
                    client_secret: "bindCardClientSecret",
                    internal_id: "bindCardInternalId",
                });
                const internalId = getClientNextActionField(
                    completeOrderRes,
                    "internal_id"
                );

                if (internalId) {
                    this.$emit("set-internal-id", internalId);
                }

                const actionResult = await handleClientNextAction(
                    completeOrderRes,
                    {
                        onHtmlForm: async (next) => {
                            this.$emit("show-iframe", next.html_form);
                            this.close();
                        },
                        beforeRedirect: async () => {
                            if (internalId) {
                                await this.$store.dispatch("redirect/setRedirect", {
                                    flow: "balanceConfirmAndReturn",
                                    context: { internal_id: internalId },
                                });
                            }
                        },
                        onRedirect: async (next) => {
                            willRedirect = true;
                            window.location.href = next.redirect_url;
                        },
                    }
                );
                if (actionResult.handled) {
                    return;
                }

                if (!this.bindCardClientSecret) {
                    this.$emit("toast", this.$t("tMsg.paymentFailed"));
                    return;
                }

                this.isShowBindPopup = true;
            } catch (error) {
                this.$emit(
                    "toast",
                    {
                        message: this.$t("tMsg.paymentFailed") + ": " + error.message,
                        persistent: !!error?.message,
                    }
                );
            } finally {
                if (!willRedirect) {
                    this.$emit("hide-loading", loadingMeta);
                }
            }
        },
        handlePrefetchBindCard(method) {
            const payMethodType = method && method.method;
            const gatewayId = this.getGatewayIdByPayMethodType(payMethodType);
            if (!gatewayId) return;
            this.bindCardGatewayId = gatewayId;
            this.$refs.bindCardPopup?.preloadBindCard({
                prepareIntent: false,
            });
        },
        async handleNewCardSubmit({ gateway, card, event }) {
            const internalId = this.bindCardInternalId;
            if (!internalId) {
                this.$emit("toast", this.$t("tMsg.paymentFailed"));
                return;
            }
            const loadingMeta = this.bindCardLoadingMeta;
            try {
                if (gateway === "stripe") {
                    this.$emit("show-loading", loadingMeta);
                    if (!this.stripe || !this.bindCardClientSecret) {
                        this.$emit("toast", this.$t("tMsg.paymentFailed"));
                        return;
                    }
                    const res = await this.stripe.confirmCardPayment(
                        this.bindCardClientSecret,
                        { payment_method: { card } }
                    );
                    if (res?.error) {
                        throw res.error;
                    }
                }

                if (gateway === "fortis") {
                    if (!event) {
                        this.$emit("toast", this.$t("tMsg.paymentFailed"));
                        return;
                    }
                }

                this.closeBindCardPopup();
                this.close();
                this.$emit("recharge-success", internalId);
            } catch (error) {
                this.$emit(
                    "toast",
                    {
                        message: this.$t("tMsg.paymentFailed") + ": " + error.message,
                        persistent: !!error?.message,
                    }
                );
            } finally {
                this.$emit("hide-loading", loadingMeta);
            }
        },
        async submitBalanceRecharge() {
            if (!this.isTopUpMethodSelected) {
                return;
            }

            const payMethodType = this.selectedMethod;
            const loadingMeta = {
                source: "recharge",
                loadingScope: "global",
                pay_method_type: payMethodType,
                selected_card_id: this.selectedCardId,
            };

            const payload = this.buildCreateOrderPayload(payMethodType);
            if (!payload) return;

            let willRedirect = false;
            try {
                this.$emit("show-loading", loadingMeta);

                const completeOrderRes = await this.doBalancePayment(
                    payload
                );

                const internalId = getClientNextActionField(
                    completeOrderRes,
                    "internal_id"
                );

                if (internalId) {
                    this.$emit("set-internal-id", internalId);
                }

                const actionResult = await handleClientNextAction(
                    completeOrderRes,
                    {
                        onHtmlForm: async (next) => {
                            this.$emit("show-iframe", next.html_form);
                            this.close();
                        },
                        beforeRedirect: async () => {
                            if (internalId) {
                                await this.$store.dispatch("redirect/setRedirect", {
                                    flow: "balanceConfirmAndReturn",
                                    context: { internal_id: internalId },
                                });
                            }
                        },
                        onRedirect: async (next) => {
                            willRedirect = true;
                            window.location.href = next.redirect_url;
                        },
                    }
                );
                if (actionResult.handled) {
                    return;
                }

                this.$emit("recharge-success", internalId);
            } catch (error) {
                this.$emit(
                    "toast",
                    {
                        message: this.$t("tMsg.paymentFailed") + ": " + error.message,
                        persistent: !!error?.message,
                    }
                );
            } finally {
                if (!willRedirect) {
                    this.$emit("hide-loading", loadingMeta);
                }
            }
        },
        buildCreateOrderPayload(payMethodType) {
            const isCardPayment =
                payMethodType === "card" || payMethodType === "web_card";

            const payload = {
                pay_method_type: payMethodType,
                amount: this.selectedAmount,
                code: this.deviceCode,
                gateway_id: 0,
                card_id: 0,
            };

            if (isCardPayment) {
                payload.card_id = this.selectedCardId;
                return payload;
            }

            const gatewayId = this.getGatewayIdByPayMethodType(payMethodType);
            if (!gatewayId) {
                this.$emit("toast", this.$t("tMsg.paymentMethodNotSupported"));
                return undefined;
            }

            payload.gateway_id = gatewayId;
            return payload;
        },
    },
};
</script>

<style lang="scss" scoped>
@use "@/styles/variables.scss" as *;

.balance-popup {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
    padding: 32px 16px 110px;
    position: relative;

    .close-icon {
        position: absolute;
        top: 13px;
        right: 12px;
        width: 34px;
        height: 34px;
        padding: 5px;
    }

    .balance-popup-title {
        font-size: 20px;
        font-weight: 590;
        line-height: 23.87px;
    }

    .balance-popup-subtitle {
        font-size: 14px;
        line-height: 17.78px;
        color: #9b9b9b;
        margin-top: 15px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        gap: 5px;

        .tax-hint {
            font-size: 12px;
            line-height: 16px;
            color: #8a8a8a;
        }
    }

    .recharge-amount-container {
        display: flex;
        align-items: center;
        justify-content: space-around;
        gap: 10px;
        margin: 20px 0;
        width: 85%;
        flex-wrap: wrap;

        .recharge-amount-item {
            padding: 9px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            flex: 1;
            border: 1px solid #e6e6e6;
            border-radius: 5px;
            font-size: 18px;
            font-weight: 500;
            line-height: 20.04px;
            color: #666666;

            &.selected {
                color: var(--theme-selectable-accent-color);
                border: 1px solid var(--theme-selectable-accent-color);
                background: var(--theme-selectable-accent-bg);
            }

            .bonus {
                font-size: 11px;
                line-height: 12.25px;
                color: var(--theme-selectable-accent-color);
            }
        }
    }

    .payment-btn {
        width: $container-width;
        position: fixed;
        bottom: 30px;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        align-items: center;
        justify-content: center;

        &.invalid {
            background: #e3e3e3;
            color: #a9a9a9;
            pointer-events: none;
        }
    }
}
</style>
